/**
 * A* по явному графу RVF (~узлов — десятки/сотни: микросекунды).
 * Свой, а не библиотека, из-за профилей проходимости рёбер (CONCEPT §2.2).
 */
import { haversineM } from '@app/geo'
import {
  type BuiltGraph,
  edgeAllows,
  edgeCostM,
  edgeProfileFactor,
  type RouteProfile,
} from './graph'

interface QueueItem {
  readonly nodeId: string
  readonly priority: number
}

/** Мини-куча по priority (граф мал — важна корректность, не константа). */
class MinHeap {
  private readonly items: QueueItem[] = []

  push(item: QueueItem): void {
    this.items.push(item)
    this.items.sort((a, b) => a.priority - b.priority)
  }

  pop(): QueueItem | undefined {
    return this.items.shift()
  }

  get size(): number {
    return this.items.length
  }
}

interface SearchState {
  readonly graph: BuiltGraph
  readonly profile: RouteProfile
  /** Строгий режим доступа (venue.routing.implied_access=false). */
  readonly strict: boolean
  readonly targetPoint: readonly [number, number]
  readonly gScore: Map<string, number>
  readonly cameFrom: Map<string, string>
  readonly open: MinHeap
}

function relaxNeighbors(state: SearchState, nodeId: string): void {
  const currentG = state.gScore.get(nodeId) ?? Number.POSITIVE_INFINITY
  const currentNode = state.graph.nodes.get(nodeId)
  for (const next of state.graph.adj.get(nodeId) ?? []) {
    if (!edgeAllows(next.edge, state.profile, next.reverse, state.strict)) continue
    const nextNode = state.graph.nodes.get(next.to)
    if (!nextNode) continue
    // Межуровневые рёбра дороже дистанции (№9); чужой тип дороги дороже своего.
    const distM = next.distM * edgeProfileFactor(next.edge, state.profile)
    const tentative =
      currentG + (currentNode ? edgeCostM(distM, currentNode, nextNode, state.graph.prefer) : distM)
    if (tentative >= (state.gScore.get(next.to) ?? Number.POSITIVE_INFINITY)) continue
    state.gScore.set(next.to, tentative)
    state.cameFrom.set(next.to, nodeId)
    state.open.push({
      nodeId: next.to,
      priority: tentative + haversineM(nextNode.point, state.targetPoint),
    })
  }
}

/** Кратчайший путь from→to как список id узлов, null если пути нет. */
export function astar(
  graph: BuiltGraph,
  fromId: string,
  toId: string,
  profile: RouteProfile,
  strict = false,
): string[] | null {
  const target = graph.nodes.get(toId)
  const start = graph.nodes.get(fromId)
  if (!start || !target) return null

  const state: SearchState = {
    graph,
    profile,
    strict,
    targetPoint: target.point,
    gScore: new Map([[fromId, 0]]),
    cameFrom: new Map(),
    open: new MinHeap(),
  }
  state.open.push({ nodeId: fromId, priority: haversineM(start.point, target.point) })
  const closed = new Set<string>()

  while (state.open.size > 0) {
    const current = state.open.pop()
    if (!current) break
    if (current.nodeId === toId) return reconstruct(state.cameFrom, toId)
    if (closed.has(current.nodeId)) continue
    closed.add(current.nodeId)
    relaxNeighbors(state, current.nodeId)
  }
  return null
}

function reconstruct(cameFrom: ReadonlyMap<string, string>, toId: string): string[] {
  const path = [toId]
  let cursor = toId
  for (;;) {
    const prev = cameFrom.get(cursor)
    if (prev === undefined) break
    path.push(prev)
    cursor = prev
  }
  return path.reverse()
}
